/**
 * box.jsx
 *
 * 为 modal、poptip、tooltip 提供基础支持
 * @Author: jruif
 * @Date: 2017/7/10 上午10:48
 */
import '../util/closest';
import React, { Fragment, isValidElement, cloneElement, Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import addEventListener from '../util/addEventListener';
import keyboardKey from '../util/keyboardKey';
// import Trigger from './trigger';

// let clickOnChildBox = false;
class Box extends Component {
  static zIndex = 10;

  static propTypes = {
    /** 主要内容 */
    children: PropTypes.node.isRequired,

    /** 额外样式 */
    className: PropTypes.string,

    /** 点击document是否关闭 */
    isCloseOnDocumentClick: PropTypes.bool,

    /** 按ESC键是否关闭 */
    isCloseOnEscape: PropTypes.bool,

    /**
     * Controls whether or not the box should close when mousing out of the box.
     * NOTE: This will prevent `closeOnTriggerMouseLeave` when mousing over the
     * gap from the trigger to the box.
     */
    isCloseOnBoxMouseLeave: PropTypes.bool,

    /** 在trigger上失焦是否关闭 */
    isCloseOnTriggerBlur: PropTypes.bool,

    /** 在trigger上点击是否关闭 */
    isCloseOnTriggerClick: PropTypes.bool,

    /** 在trigger上Mouse Leave是否关闭 */
    isCloseOnTriggerMouseLeave: PropTypes.bool,

    // defaultOpen: PropTypes.bool,

    /** 挂载节点 */
    mountNode: PropTypes.any,

    /** mouse over 时打开需要延迟时间（毫秒） */
    mouseEnterDelay: PropTypes.number,

    /** mouse leave 时打开需要延迟时间（毫秒） */
    mouseLeaveDelay: PropTypes.number,

    /**
     * 关闭的时候回调
     *
     * @param {SyntheticEvent} event - React's original SyntheticEvent.
     * @param {object} data - 所有 props.
     */
    onClose: PropTypes.func,
    disabled: PropTypes.bool,

    /**
     * 被挂载在DOM中时候回调
     *
     * @param {null}
     * @param {object} data - 所有 props.
     */
    onMount: PropTypes.func,

    /**
     * 当显示当时候回调
     *
     * @param {SyntheticEvent} event - React's original SyntheticEvent.
     * @param {object} data - 所有 props.
     */
    onOpen: PropTypes.func,

    /**
     * 从DOM中卸载的时候调用
     *
     * @param {null}
     * @param {object} data - 所有 props.
     */
    onUnmount: PropTypes.func,

    /** 是否显示 */
    isOpen: PropTypes.bool,

    /** 当trigger被点击时，是否打开 */
    isOpenOnTriggerClick: PropTypes.bool,

    /** 当trigger被focus时，是否打开 */
    isOpenOnTriggerFocus: PropTypes.bool,

    /** 当trigger被Mouse Enter时，是否打开 */
    isOpenOnTriggerMouseEnter: PropTypes.bool,

    /** 提示框等会用 */
    trigger: PropTypes.node,
  };

  static defaultProps = {
    isOpen: false,
    disabled: false,
    isCloseOnDocumentClick: false,
    isCloseOnEscape: true,
    isCloseOnRootNodeClick: false,
    isCloseOnBoxMouseLeave: false,
    isCloseOnTriggerBlur: true,
    isCloseOnTriggerClick: true,
    isCloseOnTriggerMouseLeave: false,
    isPrepend: false,
    isOpenOnTriggerClick: false,
    isOpenOnTriggerFocus: false,
    isOpenOnTriggerMouseEnter: false,
    isCacheInNodeTree: false,
    mouseEnterDelay: 0,
    mouseLeaveDelay: 0,
    mountNode: document.body,
  };

  constructor(props, context) {
    super(props, context);
    this.portalNode = null;
    this.rootNode = null;
    this.clickOnChildBox = false;
    // 初始化的时候设置实例zIndex
    // 不直接使用静态属性，为防止一个实例更新时导致所有实例都更新
    // 只有再次执行open方法都时候执行更新
    this.zIndex = Box.zIndex++;
  }

  componentWillReceiveProps(nextProps) {
    if (!nextProps.trigger && nextProps.isOpen && !this.props.isOpen) {
      this.open({});
    }
  }

  componentWillUnmount() {
    const { onUnmount } = this.props;
    this.unmountBox();
    if (onUnmount) {
      onUnmount(null, this.props);
    }
  }

  // --------------------
  // Document事件
  // ---------------------
  handleDocumentClick = e => {
    const { isCloseOnDocumentClick } = this.props;
    if (
      !this.rootNode || // not mounted
      !this.BoxNode || // no Box
      (this.triggerNode && this.triggerNode.contains(e.target)) || // event happened in trigger (delegate to trigger handlers)
      (this.rootNode && this.rootNode.contains(e.target)) // || // event happened in the box
      // this.clickOnChildBox
    ) {
      // this.clickOnChildBox = false;
      return;
    }

    if (isCloseOnDocumentClick && !this.rootNode.contains(e.target)) {
      this.close(e);
    }
  };

  // 停止冒泡
  // fix bug: box 中有新 box
  // handleBoxClick = e => {
  //     this.clickOnChildBox = true;
  //     console.log(e.target, this.BoxNode);
  // };

  // 按击'Escape'键
  handleEscape = e => {
    if (!this.props.isCloseOnEscape) return;
    if (keyboardKey.getCode(e) !== keyboardKey.name.Escape) return;

    this.close(e);
  };

  // ---------------------
  // 事件回调
  // ---------------------
  handleBoxMouseLeave = e => {
    const { isCloseOnBoxMouseLeave, mouseLeaveDelay } = this.props;
    // debugger;
    const didFocusTrigger =
      this.triggerNode &&
      e.relatedTarget !== window &&
      this.triggerNode.contains(e.relatedTarget);

    if (!isCloseOnBoxMouseLeave || didFocusTrigger) return;

    this.mouseLeaveTimer = this.closeWithTimeout(e, mouseLeaveDelay);
  };

  handleBoxMouseEnter = () => {
    const { isCloseOnBoxMouseLeave } = this.props;

    if (!isCloseOnBoxMouseLeave) return;

    clearTimeout(this.mouseLeaveTimer);
  };

  handleTriggerFocus = e => {
    const { trigger, isOpenOnTriggerFocus } = this.props;

    // 调用原生事件
    trigger.props.onFocus && trigger.props.onFocus(e);
    if (!isOpenOnTriggerFocus) return;

    // react销毁了事件对象的所有属性
    // 所以有些事件的属性(e.g currentTarget)请求到却是null，所有这里我们直接clone
    this.open({ ...e });
  };

  handleTriggerBlur = e => {
    const { trigger, isCloseOnTriggerBlur } = this.props;

    // 调用原生事件
    trigger.props.onBlur && trigger.props.onBlur(e);

    // 焦点是否在box中
    const didFocusBox = this.rootNode && this.rootNode.contains(e.relatedTarget);

    if (!isCloseOnTriggerBlur || didFocusBox) return;

    this.close(e);
  };

  handleTriggerClick = e => {
    const { trigger, isCloseOnTriggerClick, isOpenOnTriggerClick, isOpen } = this.props;
    e.stopPropagation();
    // 调用原生事件
    trigger.props.onClick && trigger.props.onClick(e);

    if (isOpen && isCloseOnTriggerClick) {
      this.close(e);
    } else if (!isOpen && isOpenOnTriggerClick) {
      this.open({ ...e });
    }
  };

  handleTriggerMouseLeave = e => {
    clearTimeout(this.mouseEnterTimer);

    const { trigger, isCloseOnTriggerMouseLeave, mouseLeaveDelay } = this.props;

    trigger.props.onMouseLeave && trigger.props.onMouseLeave(e);

    if (!isCloseOnTriggerMouseLeave) return;

    this.mouseLeaveTimer = this.closeWithTimeout(e, mouseLeaveDelay);
  };

  handleTriggerMouseEnter = e => {
    clearTimeout(this.mouseLeaveTimer);

    const { trigger, mouseEnterDelay, isOpenOnTriggerMouseEnter } = this.props;

    // Call original event handler
    trigger.props.onMouseEnter && trigger.props.onMouseEnter(this.handleTriggerMouseEnter);

    if (!isOpenOnTriggerMouseEnter) return;

    this.mouseEnterTimer = this.openWithTimeout(e, mouseEnterDelay);
  };

  // ---------------------
  // 行为
  // ---------------------
  open = e => {
    const { onOpen, disabled } = this.props;
    // 打开的时候更新实例zIndex，设置为最高层
    if (!disabled) {
      this.zIndex = Box.zIndex++;
      Promise.resolve(e).then(e => {
        if (onOpen) {
          onOpen(e, this.props);
        }
      });
    }
  };

  openWithTimeout = (e, delay) => {
    // react销毁了事件对象的所有属性，并且如果你需要异步使用事件建议使用e.persist()，
    // 所以有些事件的属性(e.g currentTarget)请求到却是null，所有这里我们直接clone
    const eventClone = { ...e };
    return setTimeout(() => this.open(eventClone), delay || 0);
  };

  close = e => {
    const { onClose, isOpen } = this.props;

    Promise.resolve(e).then(e => {
      if ((onClose, isOpen)) {
        onClose(e, this.props);
      }
    });
  };

  closeWithTimeout = (e, delay) => {
    // react消除了所有的事件对象，并且如果你需要异步使用事件建议使用e.persist()，
    // 然而有些事件属性(e.g. currentTarget)请求到却是null，所有这里我们直接clone
    const eventClone = { ...e };
    return setTimeout(() => {
      this.close(eventClone);
    }, delay || 0);
  };

  // ---------------------
  // Box渲染／卸载
  // ---------------------
  mountBox = () => {
    if (this.rootNode) return;

    const { prepend, mountNode, onMount } = this.props;

    this.rootNode = document.createElement('div');

    if (prepend) {
      mountNode.insertBefore(this.rootNode, mountNode.firstElementChild);
    } else {
      mountNode.appendChild(this.rootNode);
    }

    // 添加事件监听
    this.handleDocumentClickListener = addEventListener(
      document.body,
      'click',
      this.handleDocumentClick,
    );
    this.handleEscapeListener = addEventListener(document.body, 'keydown', this.handleEscape);

    if (onMount) onMount(null, this.props);
  };

  unmountBox = () => {
    if (!this.rootNode) return;

    // let rs = ReactDOM.unmountComponentAtNode(this.rootNode);
    this.rootNode.parentNode.removeChild(this.rootNode);

    // 移除事件监听
    this.handleDocumentClickListener.remove();
    this.handleEscapeListener.remove();
    this.handleDocumentClickListener = null;
    this.handleEscapeListener = null;

    // Clean up timers
    clearTimeout(this.mouseEnterTimer);
    clearTimeout(this.mouseLeaveTimer);

    this.rootNode = null;
    this.BoxNode = null;

    const { onUnmount } = this.props;
    if (onUnmount) onUnmount(null, this.props);
  };

  handleTriggerRef = c => {
    const { triggerRef, trigger } = this.props;
    this.triggerNode = ReactDOM.findDOMNode(c);
    if (triggerRef) {
      triggerRef(this.triggerNode);
    }
    trigger.props.ref && trigger.props.ref(c);
  };

  renderBox = () => {
    const { children, className /* , style */ } = this.props;

    this.mountBox();
    this.rootNode.className = className || '';
    const ChildCopy = React.cloneElement(children, {
      ref: instance => {
        const { ref } = children;
        this.BoxNode = instance;
        ref && ref(instance);
      },
      // onClick: this.handleBoxClick,
      onMouseEnter: this.handleBoxMouseEnter,
      onMouseLeave: this.handleBoxMouseLeave,
      className: classnames('box-content', children.props.className),
      style: Object.assign({ zIndex: this.zIndex }, children.props.style),
    });

    return ReactDOM.createPortal(ChildCopy, this.rootNode, 'box-children');
  };

  render() {
    const { trigger } = this.props;
    return (
      <Fragment>
        {isValidElement(trigger) &&
          cloneElement(trigger, {
            ref: this.handleTriggerRef,
            onBlur: this.handleTriggerBlur,
            onClick: this.handleTriggerClick,
            onFocus: this.handleTriggerFocus,
            onMouseLeave: this.handleTriggerMouseLeave,
            onMouseEnter: this.handleTriggerMouseEnter,
            className: classnames('box-trigger', trigger.props.className),
          })}
        {this.renderBox()}
      </Fragment>
    );
  }
}

export default Box;



// WEBPACK FOOTER //
// ./src/box/Box.jsx